import { useState, type FormEvent, type ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  useCreateSecurityGroup,
  useUpdateSecurityGroup,
} from '@/hooks/api/useSecurityGroups';
import type { SecurityGroup } from '@kirin/api-client/contracts/security-groups';

// Mirrors the contract limits in
// packages/api-client/src/contracts/security-groups.ts so the field caps and
// the server-side validation agree.
const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

/**
 * Create / edit dialog for a security group (KIR-3800, AC #2 and #4).
 *
 * One component serves both modes: passing `group` switches it to edit and
 * pre-fills the current values.
 */
export function SecurityGroupFormDialog({
  isOpen,
  onClose,
  organizationId,
  group,
}: {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  /** Omit to create; provide to edit that group. */
  group?: SecurityGroup;
}): ReactElement {
  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      {/*
        The form body mounts only while the dialog is open, and is keyed by the
        group being edited. That seeds its fields from props on mount, so
        reopening after a cancel (or switching which group is edited) starts
        clean without an effect syncing state — which the lint rule forbids.
      */}
      {isOpen ? (
        <SecurityGroupForm
          key={group?.id ?? 'new'}
          organizationId={organizationId}
          group={group}
          onClose={onClose}
        />
      ) : null}
    </Dialog>
  );
}

function SecurityGroupForm({
  organizationId,
  group,
  onClose,
}: {
  organizationId: string;
  group?: SecurityGroup;
  onClose: () => void;
}): ReactElement {
  const isEditing = group !== undefined;
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const createMutation = useCreateSecurityGroup(organizationId);
  const updateMutation = useUpdateSecurityGroup(organizationId);
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleClose = (): void => {
    if (isSaving) return;
    onClose();
  };

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Group name is required');
      return;
    }

    // The contract treats an empty description as "no description" (null),
    // which is also how clearing an existing one is expressed.
    const nextDescription = description.trim() ? description.trim() : null;

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          groupId: group.id,
          data: { name: trimmedName, description: nextDescription },
        });
      } else {
        await createMutation.mutateAsync({
          name: trimmedName,
          description: nextDescription,
        });
      }
      toast({
        title: 'Success',
        description: isEditing
          ? 'Security group updated'
          : 'Security group created',
        variant: 'success',
      });
      onClose();
    } catch (err) {
      // A duplicate name comes back as a 409; surface it inline rather than as
      // a toast so it sits next to the field the admin has to change.
      setError(
        err instanceof Error ? err.message : 'Failed to save security group'
      );
    }
  };

  return (
    <DialogContent
      // Keep the dialog open while a save is in flight so the result (success
      // toast or inline error) always lands somewhere the admin can see.
      onEscapeKeyDown={event => isSaving && event.preventDefault()}
      onInteractOutside={event => isSaving && event.preventDefault()}
    >
      <form onSubmit={handleSubmit}>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit security group' : 'Create security group'}
          </DialogTitle>
          <DialogDescription>
            Security groups let you apply different policies to different sets
            of users in this organization.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="security-group-name">Name</Label>
            <Input
              id="security-group-name"
              value={name}
              maxLength={NAME_MAX_LENGTH}
              placeholder="Engineering"
              onChange={event => {
                setName(event.target.value);
                if (error) setError(null);
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="security-group-description">
              Description (optional)
            </Label>
            <Textarea
              id="security-group-description"
              value={description}
              maxLength={DESCRIPTION_MAX_LENGTH}
              placeholder="What this group is for"
              onChange={event => setDescription(event.target.value)}
            />
          </div>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving || !name.trim()}>
            {isSaving
              ? 'Saving...'
              : isEditing
                ? 'Save changes'
                : 'Create group'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
